import { FC, useEffect, useState } from "react";

type NumericInputProps = {
    id?: string;
    label?: string;
    value?: number | string;
    onChange?: (value?: number) => void;
    required?: boolean;
    allowFloat?: boolean;
    className?: string;
    debounce?: number;
    readOnly?: boolean;
    /** Inclusive bounds. A committed value outside them is clamped rather than sent on. */
    min?: number;
    max?: number;
};

function parseValue(value: string, defaultValue: number | undefined, parseAsFloat: boolean): number | undefined {
    const decimalSeparator = (1.1).toLocaleString().charAt(1);
    const americanizedValue = value.replace(decimalSeparator, ".");
    if (parseAsFloat) return Number.parseFloat(americanizedValue) ?? defaultValue;
    return Number.parseInt(americanizedValue) ?? defaultValue;
}

export const NumericInput: FC<NumericInputProps> = (props) => {
    const { id, label, value, onChange, required = false, allowFloat = true, className = "", debounce = 300, readOnly = false, min, max } = props;
    const [displayValue, setDisplayValue] = useState<string>("");

    // Clamped on commit, not on keystroke, so a partially typed number isn't fought as it's entered.
    // Fields with real bounds (a retention window that must not be zero, say) can't rely on the
    // server rejecting the value afterwards: whatever this commits is what gets saved.
    const clamp = (n: number) => {
        const lowered = max !== undefined ? Math.min(n, max) : n;
        return min !== undefined ? Math.max(lowered, min) : lowered;
    };

    const valueAsString = () => value?.toString();

    // The display is rewritten only when the clamp actually overrode the entry, for two reasons.
    // It has to happen at all because the parent may already hold the clamped number (type 400 into a
    // field capped at 365 that is already 365): `value` never changes, the [value] effect never runs,
    // and the box would otherwise sit there showing 400 while 365 is what is stored. And it has to be
    // conditional so that unbounded callers — every other NumericInput in the app — keep their exact
    // current behaviour and don't get "1.50" rewritten to "1.5" mid-typing.
    //
    // The `clamped !== value` guard replaces the callers' old `parsedVal !== value` check: comparing
    // the *clamped* number is what makes an out-of-bounds entry over an already-clamped value a
    // no-op for the parent while still correcting the box.
    const commit = (parsed: number) => {
        const clamped = clamp(parsed);
        if (clamped !== parsed) setDisplayValue(clamped.toString());
        if (clamped !== value) onChange?.(clamped);
    };

    useEffect(() => {
        setDisplayValue(valueAsString() ?? "");
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!required && (displayValue === "" || displayValue === undefined)) {
                onChange?.();
                return;
            }
            const parsedVal = parseValue(displayValue, required ? 0 : undefined, allowFloat);
            if (required) {
                if (parsedVal !== null && parsedVal !== undefined && !isNaN(parsedVal)) {
                    commit(parsedVal);
                }
                return;
            }

            if (parsedVal !== undefined && !isNaN(parsedVal)) {
                commit(parsedVal);
                return;
            }
        }, debounce);

        return () => clearTimeout(timer);
    }, [displayValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleBlur = () => {
        // Commit any pending change immediately instead of waiting for the
        // debounce — otherwise tabbing out within the debounce window throws
        // away the typed value. Falls back to the canonical display form if
        // the entry is empty/invalid/unchanged.
        if (!required && (displayValue === "" || displayValue === undefined)) {
            onChange?.();
            return;
        }
        const parsedVal = parseValue(displayValue, required ? 0 : undefined, allowFloat);
        // Compare the CLAMPED number, not the raw entry: typing 400 into a field capped at 365 that
        // already holds 365 is a no-op for the parent, and falling through below is what rewrites
        // the box to 365 instead of leaving it showing 400.
        if (parsedVal !== undefined && !isNaN(parsedVal) && clamp(parsedVal) !== value) {
            onChange?.(clamp(parsedVal));
            return;
        }
        setDisplayValue(valueAsString() ?? "");
    };

    return (
        <div className={className}>
            {label && (
                <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                </label>
            )}
            <input
                id={id}
                type="text"
                inputMode="decimal"
                value={displayValue}
                onBlur={handleBlur}
                onChange={(e) => setDisplayValue(e.target.value)}
                readOnly={readOnly}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
        </div>
    );
};
